<script setup>
import { ref, onMounted, onBeforeUnmount } from 'vue';
import { useRoadmapsStore } from '@/stores/roadmaps';
import { APP_URL } from '@/lib/site';
import DemoWindow from '@/components/demo/DemoWindow.vue';
import ScaledPreview from '@/components/demo/ScaledPreview.vue';
import CoverageChart from '@/components/demo/CoverageChart.vue';
import PrivacyLog from '@/components/site/PrivacyLog.vue';
import ShareWidget from '@/components/site/ShareWidget.vue';
import PeopleLoad from '@/components/site/PeopleLoad.vue';
import ExportWidget from '@/components/site/ExportWidget.vue';
import Icon from '@/components/ui/Icon.vue';

const store = useRoadmapsStore();

const query = window.matchMedia('(min-width: 1024px)');
const desktop = ref(query.matches);
const onChange = (e) => (desktop.value = e.matches);

onMounted(() => query.addEventListener('change', onChange));
onBeforeUnmount(() => query.removeEventListener('change', onChange));

const audiences = [
  {
    who: 'Product managers',
    text: 'A clear plan, sprint by sprint, that holds up in front of execs and delivery teams without a platform to administer.'
  },
  {
    who: 'Teams in regulated industries',
    text: 'Fintech, insurtech, healthcare and legal teams can plan without putting strategy on a third-party server, because there is no server.'
  },
  {
    who: 'Founders and early-stage teams',
    text: 'Focus across the next few quarters without the overhead of a full roadmapping platform.'
  },
  {
    who: 'Delivery leads',
    text: 'Spot overbooked people and work planned before its dependencies finish, while there is still time to move things around.'
  }
];

const extras = [
  { icon: 'calendar', title: 'Dates and a Today line', text: 'Give a roadmap a start date and sprint length. Every sprint shows its dates and today is marked on the board.' },
  { icon: 'flag', title: 'Milestones', text: 'Pin a launch, a code freeze or a regulatory deadline to its date, so it stays put when the plan moves.' },
  { icon: 'board', title: 'Swimlanes', text: 'Group projects by team or theme, and fold away the lanes you are not looking at.' },
  { icon: 'tag', title: 'Committed or not', text: 'Show how firm each piece of work is. Likely work is drawn dashed and exploratory work striped.' },
  { icon: 'alert', title: 'Dependencies', text: 'Say what depends on what, and get a warning when something is planned to start too early.' },
  { icon: 'undo', title: 'Undo and redo', text: 'Every edit can be undone, from a nudged block to a change in the settings.' },
  { icon: 'history', title: 'Baselines', text: 'Save the plan at the start of a quarter and see, project by project, how far it has drifted since.' },
  { icon: 'forward', title: 'Move the timeline on', text: 'Drop finished sprints off the front and add new ones at the end, with every date kept where it was.' },
  { icon: 'upload', title: 'CSV import', text: 'Bring projects in from a spreadsheet, or from a Jira or Linear export.' },
  { icon: 'search', title: 'Find and filter', text: 'Search the board, or fade back everything except one person, phase or status.' },
  { icon: 'moon', title: 'Dark theme', text: 'Follows your system setting, or switch it yourself from the sidebar.' },
  { icon: 'pin', title: 'Pin and archive', text: 'Keep the roadmaps you use most at the top and archive the ones you have finished with.' }
];

const faqs = [
  {
    q: 'Where are my roadmaps stored?',
    a: "In your browser's own storage (IndexedDB) on the device you use. Nothing is sent to a server, so we cannot see your roadmaps and there is nothing for us to lose or leak. Minni Map also asks the browser to keep that storage rather than clear it to free up space."
  },
  {
    q: 'How do I share a roadmap with someone?',
    a: 'Create a share link from the roadmap. The roadmap is compressed into the part of the link after the #, which browsers never send to a server. Whoever opens it sees a read-only view and can add their own copy. If they already have one, they see what changed before they replace it.'
  },
  {
    q: 'Can I put a roadmap in a slide deck or a document?',
    a: 'Yes. Export it as a PNG or SVG image, print it or save it as a PDF with a table of every project, or download a CSV for a spreadsheet. Everything is drawn in your browser.'
  },
  {
    q: 'Can I bring in work I already have somewhere else?',
    a: 'Open a CSV from a spreadsheet, Jira or Linear and Minni Map builds a roadmap from it. Common column names such as Summary, Assignee and Due date are recognised, and dates are used when there are no sprint columns.'
  },
  {
    q: 'What happens if I clear my browser data?',
    a: 'Your roadmaps are cleared with it. Use "Back up everything" in the sidebar from time to time and keep the file somewhere safe. The sidebar shows when you last backed up and reminds you when a backup is due.'
  },
  {
    q: 'Can I plan in half sprints?',
    a: "Yes. Turn on half sprints in a roadmap's settings and blocks can start or finish halfway through a sprint."
  },
  {
    q: 'Does it work offline?',
    a: 'Yes. The whole app is saved on your device on your first visit, so it opens without a connection, and you can install it like any other app. When a new version is ready, you choose when to reload.'
  },
  {
    q: 'Can I use it on my phone?',
    a: 'Planning is built for laptops and desktops, because roadmaps need room to breathe. Links people share with you open on a phone too, as a read-only view.'
  },
  {
    q: 'Will my existing roadmaps still work?',
    a: 'Yes. Roadmaps, backup files and share links from earlier versions open exactly as they were. The new features are optional and stay out of the way until you use them.'
  },
  {
    q: 'Does it cost anything?',
    a: 'No. Minni Map is free to use, with no account and no trial.'
  }
];
</script>

<template>
  <div class="home">
    <section class="hero">
      <div class="wrap hero-copy">
        <a href="#whats-new" class="news"><span>New</span>Dates, milestones, undo and offline support</a>
        <h1>Private by design.<br />Fast by default.</h1>
        <p class="lead">
          A sprint roadmap that lives in your browser, not on someone else's server. No logins and no setup.
          <span class="desktop-text">Have a go with the real thing below.</span>
        </p>
        <div class="ctas">
          <a :href="APP_URL" target="_blank" rel="noopener" class="btn btn--primary">Start planning for free</a>
          <a v-if="desktop" href="#demo" class="btn btn--ghost">Try the demo</a>
        </div>
      </div>

      <div id="demo" class="stage-wrap">
        <div class="stage">
          <div class="stage-dots" aria-hidden="true" />
          <div class="stage-inner">
            <DemoWindow v-if="desktop && store.ready" />
            <template v-else-if="store.ready">
              <ScaledPreview :width="1120">
                <DemoWindow preview />
              </ScaledPreview>
              <p class="stage-note">The live demo is playable on a laptop or desktop.</p>
            </template>
          </div>
        </div>
      </div>
    </section>

    <section id="features" class="features wrap">
      <header class="section-head">
        <h2>Everything happens in your browser</h2>
        <p>The panels below are wired to the demo above, so they change as you play with it.</p>
      </header>

      <article class="feature">
        <div class="feature-copy">
          <h3>Nothing leaves your browser</h3>
          <p>
            Every roadmap is saved in your browser's own storage on the device you use. With no accounts and no servers
            holding your plans, there is nothing for anyone to lose or leak.
          </p>
          <ul class="facts">
            <li><Icon name="check" :size="15" />No sign-up or password</li>
            <li><Icon name="check" :size="15" />Works offline, and installs like an app</li>
            <li><Icon name="check" :size="15" />No requests to third parties, not even for fonts</li>
            <li><Icon name="check" :size="15" />Backup reminders, so a cleared browser is not a lost plan</li>
          </ul>
        </div>
        <div class="feature-visual"><PrivacyLog /></div>
      </article>

      <article class="feature feature--flip">
        <div class="feature-copy">
          <h3>Share a link, not a login</h3>
          <p>
            Send a roadmap to anyone with a link. The roadmap is packed into the link itself, so it never touches a
            server. Whoever opens it gets a read-only view on a laptop or a phone, and can add their own copy. If they
            already have one, they see what changed before they replace it.
          </p>
          <p class="aside">This one is real. Copy it, or open it in the app.</p>
        </div>
        <div class="feature-visual"><ShareWidget /></div>
      </article>

      <article class="feature">
        <div class="feature-copy">
          <h3>See who is stretched before they are</h3>
          <p>
            Give each person their real capacity, mark the sprints they are away and set how much of their time each
            project takes. Capacity then shows who is booked for more than they have, while there is still time to move
            things.
          </p>
        </div>
        <div class="feature-visual"><PeopleLoad /></div>
      </article>

      <article class="feature feature--flip">
        <div class="feature-copy">
          <h3>Lay roadmaps over each other</h3>
          <p>
            Compare roadmaps to find gaps where nothing is planned and overlaps where teams pile up. Roadmaps with start
            dates line up by calendar, even when they began on different days. Anyone booked in two places at once is
            flagged, and you can merge everything into one plan when you are ready.
          </p>
        </div>
        <div class="feature-visual">
          <div class="card">
            <CoverageChart v-if="store.ready" :roadmaps="store.sorted" compact />
          </div>
        </div>
      </article>

      <article class="feature">
        <div class="feature-copy">
          <h3>Take it into the meeting</h3>
          <p>
            Export any roadmap as an image for slides and documents, print it or save it as a PDF with a project table,
            or download a spreadsheet. It is all drawn in your browser, so the plan still never leaves your device.
          </p>
          <p class="aside">These buttons export the demo, changes and all.</p>
        </div>
        <div class="feature-visual"><ExportWidget v-if="store.ready" /></div>
      </article>
    </section>

    <section id="whats-new" class="extras wrap">
      <header class="section-head">
        <h2>New in Minni Map 2.1</h2>
        <p>More of the planning you actually do, still with nothing to sign up for. Existing roadmaps open exactly as they were.</p>
      </header>
      <ul class="extras-grid">
        <li v-for="x in extras" :key="x.title" class="extra">
          <span class="extra-icon"><Icon :name="x.icon" :size="17" /></span>
          <h3>{{ x.title }}</h3>
          <p>{{ x.text }}</p>
        </li>
      </ul>
    </section>

    <section class="audience wrap">
      <header class="section-head">
        <h2>Made for people who plan for a living</h2>
      </header>
      <dl class="audience-list">
        <div v-for="a in audiences" :key="a.who" class="audience-item">
          <dt>{{ a.who }}</dt>
          <dd>{{ a.text }}</dd>
        </div>
      </dl>
    </section>

    <section id="faq" class="faq wrap">
      <header class="section-head">
        <h2>Questions</h2>
      </header>
      <div class="faq-list">
        <details v-for="(f, i) in faqs" :key="f.q" class="faq-item" :open="i === 0">
          <summary>
            {{ f.q }}
            <Icon name="plus" :size="16" />
          </summary>
          <p>{{ f.a }}</p>
        </details>
      </div>
    </section>

    <section class="closing wrap">
      <div class="closing-panel">
        <div class="stage-dots" aria-hidden="true" />
        <h2>Ready to clear the noise?</h2>
        <p>No account needed. Open the app and start planning.</p>
        <a :href="APP_URL" target="_blank" rel="noopener" class="btn btn--light">Start planning for free</a>
      </div>
    </section>
  </div>
</template>

<style scoped lang="scss">
.hero {
  padding-top: 72px;
}

.hero-copy {
  max-width: 820px;
  text-align: center;

  h1 {
    font-size: clamp(40px, 6.4vw, 80px);
    font-weight: 750;
    line-height: 1.02;
    letter-spacing: -0.045em;
  }
}

.lead {
  max-width: 560px;
  margin: 22px auto 0;
  font-size: clamp(16px, 1.7vw, 19px);
  line-height: 1.55;
  color: var(--text-2);
}

.ctas {
  display: flex;
  justify-content: center;
  flex-wrap: wrap;
  gap: 10px;
  margin-top: 30px;
}

.btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  height: 46px;
  padding: 0 22px;
  border-radius: 11px;
  font-size: 15px;
  font-weight: 600;
  transition: background-color 0.15s, border-color 0.15s;

  &:hover {
    text-decoration: none;
  }

  &--primary {
    background: var(--ink);
    color: #fff;

    &:hover {
      background: var(--ink-3);
    }
  }

  &--ghost {
    border: 1px solid var(--line-strong);
    background: var(--surface);
    color: var(--text);

    &:hover {
      border-color: var(--text-3);
    }
  }

  &--light {
    background: #fff;
    color: var(--ink);

    &:hover {
      background: var(--iris-soft);
    }
  }
}

.stage-wrap {
  max-width: 1360px;
  margin: 56px auto 0;
  padding: 0 24px;
  scroll-margin-top: 80px;
}

.stage {
  position: relative;
  padding: 64px 48px;
  border-radius: 28px;
  background:
    radial-gradient(ellipse 70% 60% at 50% 0%, rgba(107, 108, 247, 0.55), transparent 70%),
    radial-gradient(ellipse 50% 50% at 100% 100%, rgba(18, 184, 154, 0.22), transparent 70%),
    var(--ink);
  overflow: hidden;
}

.stage-dots {
  position: absolute;
  inset: 0;
  background-image: radial-gradient(circle, rgba(255, 255, 255, 0.09) 1px, transparent 1px);
  background-size: 28px 28px;
  pointer-events: none;
}

.stage-inner {
  position: relative;
  max-width: 1180px;
  margin: 0 auto;
}

.stage-note {
  margin-top: 16px;
  font-size: 13px;
  color: rgba(255, 255, 255, 0.6);
  text-align: center;
}

.section-head {
  max-width: 640px;
  margin-bottom: 40px;

  h2 {
    font-size: clamp(28px, 3.4vw, 42px);
    font-weight: 700;
    line-height: 1.1;
    letter-spacing: -0.035em;
  }

  p {
    margin-top: 12px;
    font-size: 16px;
    line-height: 1.6;
    color: var(--text-2);
  }
}

.features {
  padding-top: 120px;
  scroll-margin-top: 40px;
}

.feature {
  display: grid;
  grid-template-columns: minmax(0, 5fr) minmax(0, 7fr);
  gap: 64px;
  align-items: center;
  padding: 48px 0;

  & + & {
    border-top: 1px solid var(--line);
  }

  &--flip {
    grid-template-columns: minmax(0, 7fr) minmax(0, 5fr);

    .feature-copy {
      order: 2;
    }
  }
}

.feature-copy {
  h3 {
    font-size: clamp(22px, 2.2vw, 28px);
    font-weight: 700;
    line-height: 1.2;
    letter-spacing: -0.025em;
  }

  p {
    margin-top: 12px;
    font-size: 16px;
    line-height: 1.65;
    color: var(--text-2);
  }

  .aside {
    font-size: 14px;
    color: var(--iris-strong);
    font-weight: 500;
  }
}

.facts {
  list-style: none;
  margin: 20px 0 0;
  padding: 0;

  li {
    display: flex;
    align-items: center;
    gap: 10px;
    font-size: 14px;
    font-weight: 500;

    & + li {
      margin-top: 8px;
    }
  }

  .icon {
    color: #13a37a;
  }
}

.card {
  padding: 22px;
  border: 1px solid var(--line);
  border-radius: var(--radius-l);
  background: var(--surface);
  box-shadow: 0 24px 48px -28px rgba(23, 20, 42, 0.25);
}

.news {
  display: inline-flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 26px;
  padding: 5px 14px 5px 5px;
  border: 1px solid var(--line-strong);
  border-radius: 999px;
  background: var(--surface);
  color: var(--text-2);
  font-size: 13px;
  font-weight: 500;

  span {
    padding: 2px 9px;
    border-radius: 999px;
    background: var(--iris);
    color: #fff;
    font-size: 12px;
    font-weight: 600;
  }

  &:hover {
    border-color: var(--iris);
    color: var(--text);
    text-decoration: none;
  }
}

.extras {
  padding-top: 96px;
  scroll-margin-top: 40px;
}

.extras-grid {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 16px;
  margin: 0;
  padding: 0;
  list-style: none;
}

.extra {
  padding: 22px;
  border: 1px solid var(--line);
  border-radius: var(--radius-l);
  background: var(--surface);

  h3 {
    margin-top: 14px;
    font-size: 17px;
    font-weight: 650;
    letter-spacing: -0.015em;
  }

  p {
    margin-top: 6px;
    font-size: 14px;
    line-height: 1.6;
    color: var(--text-2);
  }
}

.extra-icon {
  display: grid;
  place-items: center;
  width: 36px;
  height: 36px;
  border-radius: 10px;
  background: var(--iris-soft);
  color: var(--iris-strong);
}

.audience {
  padding-top: 96px;
}

.audience-list {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 0 48px;
  margin: 0;
}

.audience-item {
  padding: 22px 0;
  border-top: 1px solid var(--line);

  dt {
    font-family: var(--font-display);
    font-size: 19px;
    font-weight: 650;
    letter-spacing: -0.015em;
  }

  dd {
    margin: 6px 0 0;
    font-size: 15px;
    line-height: 1.6;
    color: var(--text-2);
  }
}

.faq {
  padding-top: 96px;
  scroll-margin-top: 40px;
}

.faq-list {
  max-width: 820px;
  border-top: 1px solid var(--line);
}

.faq-item {
  border-bottom: 1px solid var(--line);

  summary {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 16px;
    padding: 20px 0;
    font-family: var(--font-display);
    font-size: 18px;
    font-weight: 600;
    letter-spacing: -0.01em;
    cursor: pointer;
    list-style: none;

    &::-webkit-details-marker {
      display: none;
    }

    .icon {
      color: var(--text-3);
      transition: transform 0.2s;
    }
  }

  &[open] summary .icon {
    transform: rotate(45deg);
  }

  p {
    max-width: 680px;
    padding-bottom: 22px;
    font-size: 15px;
    line-height: 1.65;
    color: var(--text-2);
  }
}

.closing {
  padding-top: 112px;
}

.closing-panel {
  position: relative;
  overflow: hidden;
  padding: 80px 32px;
  border-radius: 28px;
  background: radial-gradient(ellipse 60% 80% at 50% 120%, rgba(107, 108, 247, 0.6), transparent 70%), var(--ink);
  text-align: center;

  h2 {
    position: relative;
    color: #fff;
    font-size: clamp(30px, 4vw, 52px);
    font-weight: 700;
    letter-spacing: -0.04em;
  }

  p {
    position: relative;
    margin: 14px 0 28px;
    color: rgba(255, 255, 255, 0.7);
    font-size: 17px;
  }

  .btn {
    position: relative;
  }
}

@media (max-width: 1279px) {
  .stage {
    padding: 40px 24px;
  }
}

@media (max-width: 1023px) {
  .desktop-text {
    display: none;
  }

  .extras-grid {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  .stage {
    padding: 28px 18px;
  }

  .feature,
  .feature--flip {
    grid-template-columns: 1fr;
    gap: 28px;

    .feature-copy {
      order: 0;
    }
  }
}

@media (max-width: 640px) {
  .hero {
    padding-top: 44px;
  }

  .stage-wrap {
    margin-top: 40px;
    padding: 0 12px;
  }

  .stage {
    border-radius: 20px;
    padding: 18px 12px;
  }

  .features {
    padding-top: 72px;
  }

  .feature {
    padding: 36px 0;
  }

  .card {
    padding: 16px;
  }

  .audience-list,
  .extras-grid {
    grid-template-columns: 1fr;
  }

  .news {
    font-size: 12px;
  }

  .audience,
  .extras,
  .faq,
  .closing {
    padding-top: 72px;
  }

  .closing-panel {
    padding: 56px 20px;
    border-radius: 20px;
  }
}
</style>
